import { ReservationType, ReservationTypeByEvents, UserHistoryItem } from '@user-history/entities/user-history-item';
import { 
  DetailReservation, HistoryBookingDetail, ReservationAssistance, ReservationExtra, ReservationHotel 
} from '@user-history/entities/history-booking-detail';
import { CALENDAR_ONE_DAY } from '@shared/base-search-bar/components/calendar/mappers/calendar-date.mapper';
import { CrossSellingReservationItem } from '@shared/cross-selling/entities/cross-selling';
import { isEmpty } from '@app/util/util';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { isAnValidActivitySource } from '@activities/mappers/activities-type.mapper';

export const TRAVELEXTRA = 'TRAVELEXTRA';

export function getReservationType(detail: HistoryBookingDetail): ReservationType {
  if (isPackageAirReservationInDetail(detail)) return ReservationType.AirPackage;
  else if (isTravelExtraReservationInDetail(detail)) return ReservationType.Extra;
  else {
    const purify = detail?.Reservations?.filter(item => item.ReservationType !== ReservationType.Assistance);
    return purify?.[0].ReservationType as ReservationType;
  }
}

export function isPackageAirReservation(item: UserHistoryItem): boolean {
  return item.ReservationItems.some(reservation => reservation.ReservationItemType === ReservationType.Flight) &&
    item.ReservationItems.some(reservation => reservation.ReservationItemType === ReservationType.Hotel);
}

export function isTravelExtraReservation(item: UserHistoryItem): boolean {
  const items = item?.ReservationItems?.filter(reservation => reservation.ReservationItemType !== ReservationType.Assistance);
  return items?.length >= 2 && items.some(reservation => 
    reservation.ReservationItemType === ReservationType.Extra || 
    reservation.ReservationItemType?.toUpperCase() === TRAVELEXTRA
  );
}

export function isTravelExtraReservationInDetail(detail: HistoryBookingDetail): boolean {
  const items = detail.Reservations?.filter(reservation => reservation.ReservationType !== ReservationType.Assistance);
  return items?.length >= 1 && items.some(reservation => 
    reservation.ReservationType === ReservationType.Extra ||
    reservation.ReservationType?.toUpperCase() === TRAVELEXTRA
  );
}

function isPackageAirReservationInDetail(detail: HistoryBookingDetail): boolean {
  return !!detail?.Reservations?.length && havePackagesInReservation(detail.Reservations);
}

function havePackagesInReservation(reservations: DetailReservation[]): boolean {
  return !!reservations?.length &&
    reservations.some(reservation => reservation.ReservationType === ReservationType.Flight) &&
    reservations.some(reservation => reservation.ReservationType === ReservationType.Hotel);
}

function haveByhoursInReservation(reservations: DetailReservation[]): boolean {
  return !!reservations?.length &&
    (reservations as ReservationHotel[]).some(reservation => 
      reservation.ReservationType === ReservationType.Hotel && !!reservation?.MicroStay);
}

export function getAllReservationTypes(reservations: DetailReservation[], assistance: ReservationAssistance): string {
  const assistanceTypeItem = !isEmpty(assistance) ? [assistance.ReservationType] : [];
  if (
    isTravelExtraReservationInDetail({ Reservations: reservations } as HistoryBookingDetail) &&
    [...reservations].every(e => isAnValidActivitySource((e as ReservationExtra)?.BookingSource))
  ) {
    return [...reservations]?.map(() => SearchBy.ACTIVITIES)?.toString();
  } else if (havePackagesInReservation(reservations)) {
    const otherElements = [...reservations]
      .filter(item =>
        item.ReservationType !== ReservationType.Flight &&
        item.ReservationType !== ReservationType.Hotel
      ).map(item => item.ReservationType);
    return [ReservationTypeByEvents[ReservationType.AirPackage]].concat([...otherElements, ...assistanceTypeItem]).toString();
  } if (haveByhoursInReservation(reservations)) {
    return SearchBy.BYHOURS;
  } else {
    const allReservations = [...reservations].map(item => {
      return item.ReservationType === ReservationType.Flight ?
        ReservationTypeByEvents[item.ReservationType] : item.ReservationType;
    });
    return allReservations.concat(assistanceTypeItem).toString();
  }
}

export function getHotelCrossellDeeplink(collection: CrossSellingReservationItem[]): string {
  const hotelCrossSelling = collection?.find(crossSelling => crossSelling.TypeCrossSellingItem === ReservationType.Hotel);
  return hotelCrossSelling ? hotelCrossSelling.UrlParams : null;
}

export function getDaysTillTripStart(departureDate: Date): number {
  const today = new Date();
  if (
    departureDate?.getSeconds() === 0 &&
    departureDate?.getMinutes() === 0 &&
    departureDate?.getHours() === 0
  ) {
    const seconds = today.getSeconds();
    const minutes = today.getMinutes();
    const hour = today.getHours();
    departureDate?.setHours(hour, minutes, seconds);
  }
  const differenceInTime = departureDate?.getTime() - today.getTime();
  const differenceInDays = Math.round(differenceInTime) / CALENDAR_ONE_DAY;
  const numberValue = Number(differenceInDays.toFixed(0));
  return numberValue < 0 ? 0 : numberValue;
}
